export type BlockIdentity = { index: number; key: string; type: string; label?: string };

export type BlockViewDetector = {
  observe: (element: Element, block: BlockIdentity) => void;
  unobserve: (element: Element) => void;
  dispose: () => void;
};

type TrackedBlock = {
  block: BlockIdentity;
  inView: boolean;
  timerId: number | null;
};

const IN_VIEW_RATIO = 0.5;
const DWELL_TIME = 1_000;
const THRESHOLDS = Array.from({ length: 21 }, (_, i) => i / 20);

const isDocumentVisible = () => document.visibilityState === 'visible';

const isZeroSize = ({ width, height }: DOMRectReadOnly) => width === 0 || height === 0;

const isInView = (entry: IntersectionObserverEntry) => {
  if (isZeroSize(entry.boundingClientRect) || !entry.isIntersecting) {
    return false;
  }

  const rootHeight = entry.rootBounds?.height ?? 0;

  return entry.intersectionRatio >= IN_VIEW_RATIO || (rootHeight > 0 && entry.intersectionRect.height >= rootHeight / 2);
};

export const createBlockViewDetector = (onBlockViewed: (block: BlockIdentity) => void): BlockViewDetector => {
  const tracked = new Map<Element, TrackedBlock>();
  const viewedKeys = new Set<string>();
  const sizeWatchers = new Map<Element, ResizeObserver>();

  const cancel = (state: TrackedBlock) => {
    if (state.timerId === null) {
      return;
    }

    window.clearTimeout(state.timerId);
    state.timerId = null;
  };

  const stopWatchingSize = (element: Element) => {
    sizeWatchers.get(element)?.disconnect();
    sizeWatchers.delete(element);
  };

  const watchSize = (element: Element, observer: IntersectionObserver) => {
    if (sizeWatchers.has(element) || typeof ResizeObserver === 'undefined') {
      return;
    }

    const watcher = new ResizeObserver((entries) => {
      if (entries.every((entry) => isZeroSize(entry.contentRect))) {
        return;
      }

      stopWatchingSize(element);
      // A block that fills the viewport before and after gaining size crosses no threshold, so only re-observing yields a fresh entry
      observer.unobserve(element);
      observer.observe(element);
    });

    watcher.observe(element);
    sizeWatchers.set(element, watcher);
  };

  const release = (element: Element, state: TrackedBlock, observer: IntersectionObserver) => {
    cancel(state);
    stopWatchingSize(element);
    tracked.delete(element);
    observer.unobserve(element);
  };

  const schedule = (element: Element, state: TrackedBlock, observer: IntersectionObserver) => {
    if (state.timerId !== null || !state.inView || !isDocumentVisible()) {
      return;
    }

    state.timerId = window.setTimeout(() => {
      state.timerId = null;

      if (!state.inView || !isDocumentVisible() || viewedKeys.has(state.block.key)) {
        return;
      }

      viewedKeys.add(state.block.key);
      release(element, state, observer);
      onBlockViewed(state.block);
    }, DWELL_TIME);
  };

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        const state = tracked.get(entry.target);

        if (!state) {
          continue;
        }

        state.inView = isInView(entry);

        if (state.inView) {
          schedule(entry.target, state, observer);
        } else {
          cancel(state);
        }

        if (isZeroSize(entry.boundingClientRect)) {
          watchSize(entry.target, observer);
        } else {
          stopWatchingSize(entry.target);
        }
      }
    },
    { root: null, threshold: THRESHOLDS },
  );

  const handleVisibilityChange = () => {
    for (const [element, state] of tracked) {
      if (isDocumentVisible()) {
        schedule(element, state, observer);
      } else {
        cancel(state);
      }
    }
  };

  document.addEventListener('visibilitychange', handleVisibilityChange);

  return {
    observe: (element, block) => {
      if (tracked.has(element) || viewedKeys.has(block.key)) {
        return;
      }

      tracked.set(element, { block, inView: false, timerId: null });
      observer.observe(element);
    },
    unobserve: (element) => {
      const state = tracked.get(element);

      if (state) {
        release(element, state, observer);
      }
    },
    dispose: () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);

      for (const state of tracked.values()) {
        cancel(state);
      }

      for (const watcher of sizeWatchers.values()) {
        watcher.disconnect();
      }

      tracked.clear();
      sizeWatchers.clear();
      observer.disconnect();
    },
  };
};
